import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useDbUser } from '../hooks/useDbUser';
import { MatterList } from '../components/matters/MatterList';
import { Matter, Goal, Task, Project, Client, MatterType, db } from '../lib/db';
import { toast } from 'sonner';

export function MattersPage() {
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  const location = useLocation();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [matters, setMatters] = useState<Matter[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [matterTypes, setMatterTypes] = useState<MatterType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [openMatterId, setOpenMatterId] = useState<string | null>(null);

  const fetchData = async () => {
    if (!dbUserId) return;

    setIsLoading(true);
    try {
      const [fetchedMatters, fetchedGoals, fetchedTasks, fetchedProjects, fetchedClients, fetchedMatterTypes] = await Promise.all([
        db.matters.findByUserId(dbUserId, showArchived),
        db.goals.findByUserId(dbUserId),
        db.tasks.findByUserId(dbUserId),
        db.projects.findByUserId(dbUserId),
        db.clients.findByUserId(dbUserId),
        db.matterTypes.findByUserId(dbUserId),
      ]);
      setMatters(fetchedMatters);
      // Filter goals by selected year
      const yearGoals = fetchedGoals.filter(goal => goal.year === selectedYear);
      setGoals(yearGoals);
      setTasks(fetchedTasks);
      setProjects(fetchedProjects);
      setClients(fetchedClients);
      setMatterTypes(fetchedMatterTypes);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!dbUserLoading && dbUserId) {
      fetchData();
    }
  }, [dbUserId, dbUserLoading, selectedYear, showArchived]);

  useEffect(() => {
    // Check if we need to open a specific matter from navigation
    if (location.state?.openMatterId) {
      setOpenMatterId(location.state.openMatterId);
      // Clear the state so it doesn't persist on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const handleMatterMarkComplete = async (matter: Matter) => {
    if (!dbUserId) return;
    
    try {
      const updatedMatter = await db.matters.markComplete(matter.id, dbUserId);
      setMatters(matters.map(m => m.id === matter.id ? updatedMatter : m));
      toast.success('Matter marked as complete');
    } catch (error) {
      console.error('Error marking matter as complete:', error);
      toast.error('Failed to mark matter as complete');
    }
  };
  
  const handleMatterMarkIncomplete = async (matter: Matter) => {
    if (!dbUserId) return;
    
    try {
      const updatedMatter = await db.matters.markIncomplete(matter.id, dbUserId);
      setMatters(matters.map(m => m.id === matter.id ? updatedMatter : m));
      toast.success('Matter marked as incomplete');
    } catch (error) {
      console.error('Error marking matter as incomplete:', error);
      toast.error('Failed to mark matter as incomplete');
    }
  };

  if (dbUserLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (!dbUserId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-gray-500">Please log in to view matters.</div>
      </div>
    );
  }

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() + i
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Mobile Layout */}
      <div className="sm:hidden">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold text-gray-900">Matters</h1>
          <div className="flex items-center gap-2">
            <label htmlFor="year-mobile" className="text-sm font-medium text-gray-700">
              Year:
            </label>
            <select
              id="year-mobile"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="block px-2 py-1 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              {years.map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden sm:block">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Matters</h1>
        </div>

        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1">
                Year
              </label>
              <select
                id="year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="block w-full px-3 py-2 text-sm border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="flex rounded-md shadow-sm" role="group">
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium rounded-l-lg border ${
                  !showArchived
                    ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => setShowArchived(false)}
              >
                Active
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium rounded-r-lg border-l-0 border ${
                  showArchived
                    ? 'bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                onClick={() => setShowArchived(true)}
              >
                Archived
              </button>
            </div>
          </div>
        </div>
      </div>

      <MatterList
        matters={matters}
        goals={goals}
        tasks={tasks}
        projects={projects}
        clients={clients}
        matterTypes={matterTypes}
        userId={dbUserId}
        onUpdate={fetchData}
        isArchiveView={showArchived}
        showArchived={showArchived}
        setShowArchived={setShowArchived}
        openMatterId={openMatterId}
        onMatterOpened={() => setOpenMatterId(null)}
      />
    </div>
  );
}